import { activityLogger } from '@databases/app-access';
import { useDialog } from '@databases/dialog';
import { messages } from '@databases/intl/messages';
import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import React, {
  createContext,
  useState,
  PropsWithChildren,
  FC,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl-next';

type AllowedPanelTypes = {
  ViewSettingsPanel: {
    autoFocusLastFilterValue?: boolean;
    canEditViews: boolean;
    unsavedChangesMessage: string;
  };
  FieldCreatePanel: {
    type: DatabaseFieldType;
    field: DatabaseField<DatabaseFieldType>;
    disableMulti?: boolean;
    enforceSelectOption?: boolean;
  };
  FieldSettingsPanel: {
    fieldId: string;
    createSelectOption?: boolean | undefined;
  };
  EntryViewPanel: {
    entryId: string;
    contentId?: string;
    noDelete?: boolean;
    isReadonly?: boolean;
  };
  DatabaseSetupPanel: undefined;
  CopyDatabasePanel: undefined;
};

type AllowedPanelKeys = keyof AllowedPanelTypes;

type PanelDataType = AllowedPanelTypes[AllowedPanelKeys];

export type PanelsType = {
  [key in AllowedPanelKeys]?: React.ReactNode;
};

export type PanelUnsavedChangesStateType = {
  [key in AllowedPanelKeys]: boolean;
};

type ShowPanelType = <key extends AllowedPanelKeys>(
  panelKey: key,
  data?: AllowedPanelTypes[key]
) => void;

type HidePanelOptions = {
  ignoreUnsavedChanges?: boolean;
  onClose?: () => void;
};

export interface PanelContextInterface {
  showPanel: ShowPanelType;
  hidePanel: (options?: HidePanelOptions) => void;
  getData: <T extends AllowedPanelKeys>() => AllowedPanelTypes[T];
  getPanelComponent: () => React.ReactNode;
  getPanelKey: () => AllowedPanelKeys | undefined;
  isPanelVisible: (panelKey?: AllowedPanelKeys) => boolean;
  setUnsavedChanges: (panelKey: string, val: boolean) => void;
}

type PanelType = {
  panelKey: AllowedPanelKeys | undefined;
  component: React.ReactNode;
  data: PanelDataType;
};

export const PanelContext = createContext<PanelContextInterface>({
  showPanel: () => {},
  hidePanel: () => {},
  getData: <T,>() => null as T,
  getPanelComponent: () => null,
  getPanelKey: () => undefined,
  isPanelVisible: () => false,
  setUnsavedChanges: () => {},
});

export const PanelProvider: FC<
  PropsWithChildren<{
    defaultPanels: PanelsType;
  }>
> = ({ children, defaultPanels }) => {
  const [panels] = useState<PanelsType>(defaultPanels);
  const [panelsUnsavedChangesState, setPanelsUnsavedChangesState] =
    useState<PanelUnsavedChangesStateType>(
      Object.keys(defaultPanels).reduce((acc, key) => {
        acc[key as AllowedPanelKeys] = false;
        return acc;
      }, {} as PanelUnsavedChangesStateType)
    );
  const [panel, setPanel] = useState<PanelType>({
    component: null,
    data: undefined,
    panelKey: undefined,
  });

  const { showDialog } = useDialog();
  const { formatMessage } = useIntl();

  // Internal functions

  const showUnsavedChangesConfirmDialog = useCallback(
    (onClose: () => void) => {
      const body =
        panel?.data && 'unsavedChangesMessage' in panel.data
          ? panel.data.unsavedChangesMessage
          : '';

      showDialog('ConfirmDialog', {
        heading: formatMessage(messages['panel.exit-dialog-heading']),
        body: body,
        yesLabel: formatMessage(messages['panel.exit-dialog-yes-label']),
        noLabel: formatMessage(messages['panel.exit-dialog-no-label']),
        appearance: 'warning',
        onClose: (result) => {
          if (result) {
            onClose();
          }
        },
      });
    },
    [formatMessage, panel.data, showDialog]
  );

  // Exposed functions
  const setUnsavedChanges = useCallback((panelKey: string, val: boolean) => {
    setPanelsUnsavedChangesState((prev) => {
      return {
        ...prev,
        [panelKey]: val,
      };
    });
  }, []);

  const showPanel: ShowPanelType = useCallback(
    (panelKey, data) => {
      if (panel.panelKey && panelsUnsavedChangesState[panel.panelKey]) {
        showUnsavedChangesConfirmDialog(() => {
          setPanel({ component: panels[panelKey], data, panelKey });
        });
      } else {
        setPanel({ component: panels[panelKey], data, panelKey });
      }
      activityLogger.log('showPanel', { panelKey: panel.panelKey, ...data });
    },
    [panel.panelKey, panels, panelsUnsavedChangesState, showUnsavedChangesConfirmDialog]
  );

  const hidePanel = useCallback(
    (
      { ignoreUnsavedChanges = false, onClose = () => {} }: HidePanelOptions = {
        ignoreUnsavedChanges: false,
        onClose: () => {},
      }
    ) => {
      if (
        !ignoreUnsavedChanges &&
        panel.panelKey &&
        panelsUnsavedChangesState[panel.panelKey]
      ) {
        showUnsavedChangesConfirmDialog(() => {
          setPanel({ component: null, data: undefined, panelKey: undefined });
          onClose();
        });
      } else {
        setPanel({ component: null, data: undefined, panelKey: undefined });
        onClose();
      }
      activityLogger.log('hidePanel', { panelKey: panel.panelKey });
    },
    [panel.panelKey, panelsUnsavedChangesState, showUnsavedChangesConfirmDialog]
  );

  const isPanelVisible = useCallback(
    (panelKey?: AllowedPanelKeys) => {
      if (!panelKey) {
        return panel.component !== null;
      }

      if (panel.component === null) {
        return false;
      }

      return panel.panelKey === panelKey;
    },
    [panel.component, panel.panelKey]
  );

  const getData = useCallback(<T,>(): T => panel.data as T, [panel.data]);
  const getPanelComponent = useCallback(() => panel.component, [panel.component]);
  const getPanelKey = useCallback(() => panel.panelKey, [panel.panelKey]);

  return (
    <PanelContext.Provider
      value={{
        showPanel,
        hidePanel,
        getData,
        getPanelComponent,
        isPanelVisible,
        getPanelKey,
        setUnsavedChanges,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};
